import React, { useState, useEffect, useCallback } from 'react';

const OverviewTab = ({
  user,
  currentUserProfile,
  tenantConfig = {},
  isMobile = false,
  setActiveTab,
  apiBase
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedTenantId, setSelectedTenantId] = useState('');

  const isOperator = user?.role === 'OPERATOR';
  const isGlobal = ['GLOBAL_ADMIN', 'GLOBAL_POWER', 'GLOBAL_STANDARD'].includes(user?.role);
  const isTenantAdmin = user?.role === 'TENANT_ADMIN';

  const isDev = typeof window !== 'undefined' && window.location.port === '3000';
  const API_BASE = apiBase || process.env.REACT_APP_API_BASE || (isDev ? `http://${window.location.hostname}:8000` : '');

  const fetchMetrics = useCallback(async (tenantFilter = null) => {
    setLoading(true);
    setError(null);
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      let url = `${API_BASE}/api/v1/overview/metrics`;
      const params = new URLSearchParams();
      if (tenantFilter) {
        params.append('tenant_id', tenantFilter);
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const res = await fetch(url, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
          'X-Tenant-Domain': typeof window !== 'undefined' ? window.location.hostname : ''
        }
      });

      let contentType = '';
      if (res.headers && typeof res.headers.get === 'function') {
        contentType = res.headers.get('content-type') || '';
      }

      if (!res.ok) {
        let errMsg = `Failed to load metrics: ${res.statusText} (${res.status})`;
        if (contentType.includes('application/json')) {
          try {
            const errJson = await res.json();
            errMsg = errJson.detail || errJson.message || errMsg;
          } catch (_) {}
        }
        throw new Error(errMsg);
      }

      if (contentType && contentType.includes('text/html')) {
        throw new Error(`Server returned HTML instead of JSON. Please ensure backend API is running at ${API_BASE}.`);
      }

      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error('[OverviewTab] Error fetching metrics:', err);
      setError(err.message || 'An error occurred while loading overview data.');
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchMetrics(selectedTenantId || null);
  }, [fetchMetrics, selectedTenantId]);

  const handleTenantChange = (e) => {
    const val = e.target.value;
    setSelectedTenantId(val);
  };

  const handleSelectTenantFromTable = (tId) => {
    setSelectedTenantId(tId.toString());
  };

  const currentMonthLabel = data?.current_month_label || 'Current Month';
  const opMetrics = data?.operator_metrics;
  const tnMetrics = data?.tenant_metrics;
  const glMetrics = data?.global_metrics;

  // Render KPI Card helper
  const renderKpiCard = ({
    label,
    value,
    currentMonthValue,
    currentMonthSuffix = 'this month',
    subtext,
    icon,
    color = 'var(--primary-color)',
    highlightCurrentMonth = true
  }) => (
    <div
      style={{
        background: 'var(--card-bg, #161b22)',
        border: '1px solid var(--border-color, #30363d)',
        borderRadius: '8px',
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        transition: 'transform 0.15s ease, border-color 0.15s ease',
        minWidth: 0,
        boxSizing: 'border-box'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
        <span
          style={{
            fontSize: '11px',
            fontWeight: '700',
            color: color,
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {label}
        </span>
        {icon && (
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '4px',
              background: 'rgba(255, 255, 255, 0.04)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
              flexShrink: 0
            }}
          >
            {React.cloneElement(icon, { width: 13, height: 13 })}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', margin: '2px 0 4px 0' }}>
        <h4
          style={{
            fontSize: '22px',
            fontWeight: '800',
            margin: 0,
            color: color,
            lineHeight: 1.1
          }}
        >
          {typeof value === 'number' ? value.toLocaleString() : value}
        </h4>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: 'auto', minWidth: 0 }}>
        {highlightCurrentMonth && currentMonthValue !== undefined && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              background: 'rgba(46, 160, 67, 0.12)',
              border: '1px solid rgba(46, 160, 67, 0.3)',
              borderRadius: '4px',
              padding: '2px 5px',
              fontSize: '10px',
              fontWeight: '600',
              color: '#3fb950',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
              <polyline points="17 6 23 6 23 12" />
            </svg>
            <span>
              +{typeof currentMonthValue === 'number' ? currentMonthValue.toLocaleString() : currentMonthValue} {currentMonthSuffix}
            </span>
          </div>
        )}

        {subtext && (
          <span
            title={subtext}
            style={{
              fontSize: '11px',
              color: 'var(--text-mute, #8b949e)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: '1 1 auto',
              minWidth: 0
            }}
          >
            {subtext}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {/* 1. Unified Overview Header & Toolbar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px',
          background: 'var(--card-bg, #161b22)',
          border: '1px solid var(--border-color, #30363d)',
          borderRadius: '8px',
          padding: '8px 14px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: 'var(--primary-color)' }}>
              Welcome back, {currentUserProfile?.first_name || 'Administrator'}!
            </h3>
            {(tenantConfig?.brand_name || tenantConfig?.domain_name) && (
              <span style={{ fontSize: '12px', color: 'var(--text-mute, #8b949e)' }}>
                — {tenantConfig.brand_name || 'TrophyCoin'}{tenantConfig?.domain_name ? ` (${tenantConfig.domain_name})` : ''}
              </span>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '3px 8px',
              borderRadius: '6px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid var(--border-color, #30363d)',
              fontSize: '11px',
              fontWeight: '600',
              color: 'var(--text-main, #c9d1d9)'
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--primary-color)' }}>
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span>Active Period: <strong style={{ color: 'var(--primary-color)' }}>{currentMonthLabel}</strong></span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {isOperator && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-mute, #8b949e)', opacity: 0.85 }}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span style={{ fontSize: '11px', color: 'var(--text-mute, #8b949e)', whiteSpace: 'nowrap' }}>
                Annual Hosting Subscription Status:
              </span>
              {currentUserProfile?.annual_subscription_expires_at ? (
                new Date(currentUserProfile.annual_subscription_expires_at) > new Date() ? (
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    backgroundColor: 'rgba(46, 160, 67, 0.15)',
                    color: '#3fb950',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    whiteSpace: 'nowrap'
                  }}>
                    Active (Expires {new Date(currentUserProfile.annual_subscription_expires_at).toLocaleDateString()})
                  </span>
                ) : (
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    backgroundColor: 'rgba(248, 81, 73, 0.15)',
                    color: '#ff7b72',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    whiteSpace: 'nowrap'
                  }}>
                    Expired {new Date(currentUserProfile.annual_subscription_expires_at).toLocaleDateString()}
                  </span>
                )
              ) : (
                <span style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  backgroundColor: 'rgba(248, 81, 73, 0.15)',
                  color: '#ff7b72',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  whiteSpace: 'nowrap'
                }}>
                  No Subscription (Auto-added to checkout)
                </span>
              )}
            </div>
          )}

          {/* Global User Tenant Selector */}
          {isGlobal && glMetrics?.tenants_list && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label htmlFor="global-tenant-select" style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute, #8b949e)', whiteSpace: 'nowrap' }}>
                Tenant Scope:
              </label>
              <select
                id="global-tenant-select"
                value={selectedTenantId}
                onChange={handleTenantChange}
                style={{
                  background: 'var(--input-bg, #0d1117)',
                  color: 'var(--text-main, #c9d1d9)',
                  border: '1px solid var(--border-color, #30363d)',
                  borderRadius: '6px',
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: '500',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Tenants (Global Aggregate)</option>
                {glMetrics.tenants_list.map((t) => (
                  <option key={t.id} value={t.id.toString()}>
                    {t.brand_name} ({t.domain_name})
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            onClick={() => fetchMetrics(selectedTenantId || null)}
            disabled={loading}
            title="Refresh Metrics"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              padding: '4px 10px',
              borderRadius: '6px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid var(--border-color, #30363d)',
              color: 'var(--text-main, #c9d1d9)',
              fontSize: '12px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ transform: loading ? 'rotate(180deg)' : 'none', transition: 'transform 0.4s ease' }}
            >
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            <span>{loading ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {/* Loading Skeleton */}
      {loading && !data && (
        <div
          style={{
            background: 'var(--card-bg, #161b22)',
            border: '1px solid var(--border-color, #30363d)',
            borderRadius: '8px',
            padding: '24px',
            textAlign: 'center',
            color: 'var(--text-mute, #8b949e)'
          }}
        >
          <div style={{ display: 'inline-block', marginBottom: '10px' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="spin-icon">
              <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="16" />
            </svg>
          </div>
          <p style={{ margin: 0, fontSize: '13px' }}>Loading real-time overview analytics...</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div
          style={{
            background: 'rgba(248, 81, 73, 0.1)',
            border: '1px solid rgba(248, 81, 73, 0.4)',
            borderRadius: '8px',
            padding: '12px 16px',
            color: '#ff7b72',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}
        >
          <span><strong>Failed to load overview data:</strong> {error}</span>
          <button
            onClick={() => fetchMetrics(selectedTenantId || null)}
            style={{
              background: '#f85149',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              padding: '4px 10px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Data Loaded Display */}
      {data && (
        <>
          {/* ---------------------------------------------------------------- */}
          {/* 2. OPERATOR ROLE METRICS VIEW                                    */}
          {/* ---------------------------------------------------------------- */}
          {isOperator && opMetrics && (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '12px'
                }}
              >
                {/* Minted Coins */}
                {renderKpiCard({
                  label: 'Minted Coins',
                  value: opMetrics.minted_coins?.total || 0,
                  currentMonthValue: opMetrics.minted_coins?.current_month || 0,
                  currentMonthSuffix: 'minted this month',
                  subtext: 'Allocated physical NFC coins in your vault',
                  color: 'var(--primary-color, #bdb2ff)',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 6v12M6 12h12" />
                    </svg>
                  )
                })}

                {/* Issued Coins */}
                {renderKpiCard({
                  label: 'Issued Coins',
                  value: opMetrics.issued_coins?.total || 0,
                  currentMonthValue: opMetrics.issued_coins?.current_month || 0,
                  currentMonthSuffix: 'issued this month',
                  subtext: 'Coins programmed and issued to end users',
                  color: '#3fb950',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  )
                })}

                {/* Total Card Views */}
                {renderKpiCard({
                  label: 'Total Card Views',
                  value: opMetrics.card_views?.total || 0,
                  currentMonthValue: opMetrics.card_views?.current_month || 0,
                  currentMonthSuffix: 'views this month',
                  subtext: 'Physical NFC chip taps and card visits',
                  color: 'var(--accent-color, #a0c4ff)',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )
                })}

                {/* Shares */}
                {renderKpiCard({
                  label: 'Card Shares',
                  value: opMetrics.card_shares?.total || 0,
                  currentMonthValue: opMetrics.card_shares?.current_month || 0,
                  currentMonthSuffix: 'shares this month',
                  subtext: 'End user shares via link, QR, or social',
                  color: '#e3b341',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                  )
                })}

                {/* Click-Through Referrals */}
                {renderKpiCard({
                  label: 'Click-Through Referrals',
                  value: opMetrics.click_through_referrals?.total || 0,
                  currentMonthValue: opMetrics.click_through_referrals?.current_month || 0,
                  currentMonthSuffix: 'clicks this month',
                  subtext: `Website (${opMetrics.click_through_referrals?.breakdown?.website?.total || 0}) · Email (${opMetrics.click_through_referrals?.breakdown?.email?.total || 0}) · Booking (${opMetrics.click_through_referrals?.breakdown?.booking?.total || 0})`,
                  color: '#a371f7',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  )
                })}
              </div>

              {/* Operator Month-by-Month Trend Section */}
              {opMetrics.monthly_trends && opMetrics.monthly_trends.length > 0 && (
                <div
                  style={{
                    background: 'var(--card-bg, #161b22)',
                    border: '1px solid var(--border-color, #30363d)',
                    borderRadius: '8px',
                    padding: '14px 16px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: 'var(--primary-color)' }}>
                        Month-by-Month Activity Trends
                      </h4>
                      <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-mute, #8b949e)' }}>
                        Progression across minted coins, card views, shares, and referrals with current month highlighted.
                      </p>
                    </div>
                  </div>

                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '600px' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border-color, #30363d)' }}>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Month</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Minted Coins</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Issued Coins</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Card Views</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Shares</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Referral Clicks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {opMetrics.monthly_trends.map((item) => (
                          <tr
                            key={item.month}
                            style={{
                              borderBottom: '1px solid var(--border-color, #30363d)',
                              backgroundColor: item.is_current ? 'rgba(189, 178, 255, 0.08)' : 'transparent',
                              fontWeight: item.is_current ? '700' : 'normal'
                            }}
                          >
                            <td style={{ padding: '8px 10px', fontSize: '12px', color: item.is_current ? 'var(--primary-color)' : 'var(--text-main)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>{item.label}</span>
                                {item.is_current && (
                                  <span
                                    style={{
                                      fontSize: '9px',
                                      fontWeight: '700',
                                      padding: '1px 5px',
                                      borderRadius: '8px',
                                      background: 'var(--primary-color)',
                                      color: '#000'
                                    }}
                                  >
                                    CURRENT
                                  </span>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '8px 10px', fontSize: '12px', color: 'var(--text-main)' }}>
                              {item.minted_coins}
                            </td>
                            <td style={{ padding: '8px 10px', fontSize: '12px', color: '#3fb950' }}>
                              {item.issued_coins}
                            </td>
                            <td style={{ padding: '8px 10px', fontSize: '12px', color: 'var(--accent-color)' }}>
                              {item.card_views}
                            </td>
                            <td style={{ padding: '8px 10px', fontSize: '12px', color: '#e3b341' }}>
                              {item.card_shares}
                            </td>
                            <td style={{ padding: '8px 10px', fontSize: '12px', color: '#a371f7' }}>
                              {item.click_through_referrals}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            </>
          )}

          {/* ---------------------------------------------------------------- */}
          {/* 3. TENANT & GLOBAL METRICS VIEW                                  */}
          {/* ---------------------------------------------------------------- */}
          {(isTenantAdmin || isGlobal) && tnMetrics && (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '12px'
                }}
              >
                {/* Number of Operators */}
                {renderKpiCard({
                  label: 'Operators',
                  value: tnMetrics.operator_count?.total || 0,
                  currentMonthValue: tnMetrics.operator_count?.current_month_new || 0,
                  currentMonthSuffix: 'new this month',
                  subtext: 'Active operator accounts managing coins',
                  color: 'var(--primary-color, #bdb2ff)',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  )
                })}

                {/* Active Coins */}
                {renderKpiCard({
                  label: 'Active Coins',
                  value: tnMetrics.active_coins?.total || 0,
                  currentMonthValue: tnMetrics.active_coins?.current_month || 0,
                  currentMonthSuffix: 'minted this month',
                  subtext: `${tnMetrics.active_coins?.breakdown?.issued_total || 0} Issued · ${tnMetrics.active_coins?.breakdown?.unissued_total || 0} Unissued`,
                  color: 'var(--accent-color, #a0c4ff)',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )
                })}

                {/* Card Shares */}
                {renderKpiCard({
                  label: 'Card Shares',
                  value: tnMetrics.card_shares?.total || 0,
                  currentMonthValue: tnMetrics.card_shares?.current_month || 0,
                  currentMonthSuffix: 'shares this month',
                  subtext: 'Total social, link, and direct QR shares',
                  color: '#e3b341',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                  )
                })}

                {/* Card Click-Throughs */}
                {renderKpiCard({
                  label: 'Card Click-Throughs',
                  value: tnMetrics.card_click_throughs?.total || 0,
                  currentMonthValue: tnMetrics.card_click_throughs?.current_month || 0,
                  currentMonthSuffix: 'clicks this month',
                  subtext: `Website (${tnMetrics.card_click_throughs?.breakdown?.website || 0}) · Email (${tnMetrics.card_click_throughs?.breakdown?.email || 0})`,
                  color: '#58a6ff',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15 3h6v6" />
                      <path d="M10 14L21 3" />
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    </svg>
                  )
                })}

                {/* Booking Click-Throughs */}
                {renderKpiCard({
                  label: 'Booking Click-Throughs',
                  value: tnMetrics.booking_click_throughs?.total || 0,
                  currentMonthValue: tnMetrics.booking_click_throughs?.current_month || 0,
                  currentMonthSuffix: 'bookings this month',
                  subtext: 'Direct customer appointments & bookings',
                  color: '#3fb950',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                      <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01" />
                    </svg>
                  )
                })}

                {/* Active Orders in Production */}
                {renderKpiCard({
                  label: 'Orders in Production',
                  value: tnMetrics.active_orders_in_production?.count || 0,
                  currentMonthValue: tnMetrics.active_orders_in_production?.current_month || 0,
                  currentMonthSuffix: 'created this month',
                  subtext: 'Packages currently manufacturing on lines',
                  color: '#ff7b72',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                      <line x1="12" y1="22.08" x2="12" y2="12" />
                    </svg>
                  )
                })}
              </div>

              {/* Tenant Month-by-Month Trend Section */}
              {tnMetrics.monthly_trends && tnMetrics.monthly_trends.length > 0 && (
                <div
                  style={{
                    background: 'var(--card-bg, #161b22)',
                    border: '1px solid var(--border-color, #30363d)',
                    borderRadius: '8px',
                    padding: '14px 16px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: 'var(--primary-color)' }}>
                        Month-by-Month Performance & Historical Trends
                      </h4>
                      <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-mute, #8b949e)' }}>
                        Historical breakdown of active coins, shares, click-throughs, bookings, and production orders, with current month displayed in all areas.
                      </p>
                    </div>
                  </div>

                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '700px' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border-color, #30363d)' }}>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Month</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Active Coins</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Issued Coins</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Card Shares</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Card Clicks</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Booking Clicks</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Orders in Prod</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tnMetrics.monthly_trends.map((item) => (
                          <tr
                            key={item.month}
                            style={{
                              borderBottom: '1px solid var(--border-color, #30363d)',
                              backgroundColor: item.is_current ? 'rgba(189, 178, 255, 0.08)' : 'transparent',
                              fontWeight: item.is_current ? '700' : 'normal'
                            }}
                          >
                            <td style={{ padding: '8px 10px', fontSize: '12px', color: item.is_current ? 'var(--primary-color)' : 'var(--text-main)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>{item.label}</span>
                                {item.is_current && (
                                  <span
                                    style={{
                                      fontSize: '9px',
                                      fontWeight: '700',
                                      padding: '1px 5px',
                                      borderRadius: '8px',
                                      background: 'var(--primary-color)',
                                      color: '#000'
                                    }}
                                  >
                                    CURRENT
                                  </span>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '8px 10px', fontSize: '12px', color: 'var(--text-main)' }}>
                              {item.active_coins}
                            </td>
                            <td style={{ padding: '8px 10px', fontSize: '12px', color: '#3fb950' }}>
                              {item.issued_coins}
                            </td>
                            <td style={{ padding: '8px 10px', fontSize: '12px', color: '#e3b341' }}>
                              {item.card_shares}
                            </td>
                            <td style={{ padding: '8px 10px', fontSize: '12px', color: '#58a6ff' }}>
                              {item.card_click_throughs}
                            </td>
                            <td style={{ padding: '8px 10px', fontSize: '12px', color: '#3fb950' }}>
                              {item.booking_click_throughs}
                            </td>
                            <td style={{ padding: '8px 10px', fontSize: '12px', color: '#ff7b72' }}>
                              {item.orders_in_production}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Active Orders in Production Queue */}
              <div
                style={{
                  background: 'var(--card-bg, #161b22)',
                  border: '1px solid var(--border-color, #30363d)',
                  borderRadius: '8px',
                  padding: '14px 16px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#ff7b72', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                        <line x1="3" y1="6" x2="21" y2="6" />
                        <path d="M16 10a4 4 0 0 1-8 0" />
                      </svg>
                      <span>Active Orders in Production</span>
                    </h4>
                    <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-mute, #8b949e)' }}>
                      Physical coin packages currently being manufactured on 3D printer lines.
                    </p>
                  </div>

                  {setActiveTab && (
                    <button
                      onClick={() => setActiveTab('mint')}
                      style={{
                        padding: '4px 10px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid var(--border-color, #30363d)',
                        borderRadius: '6px',
                        color: 'var(--primary-color)',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Open Mint Queue &rarr;
                    </button>
                  )}
                </div>

                {tnMetrics.active_orders_in_production?.orders?.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '600px' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border-color, #30363d)' }}>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Order #</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Package Name</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Customer / Operator</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Items</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Total</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Status</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tnMetrics.active_orders_in_production.orders.map((ord) => (
                          <tr key={ord.id} style={{ borderBottom: '1px solid var(--border-color, #30363d)' }}>
                            <td style={{ padding: '8px 10px', fontSize: '12px', fontWeight: '600', color: 'var(--primary-color)' }}>
                              #{ord.id}
                            </td>
                            <td style={{ padding: '8px 10px', fontSize: '12px', color: 'var(--text-main)' }}>
                              {ord.package_name}
                            </td>
                            <td style={{ padding: '8px 10px', fontSize: '12px', color: 'var(--text-main)' }}>
                              <div>{ord.ordered_by}</div>
                              {ord.ordered_by_email && (
                                <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>{ord.ordered_by_email}</div>
                              )}
                            </td>
                            <td style={{ padding: '8px 10px', fontSize: '12px', color: 'var(--text-main)' }}>
                              {ord.items_count} pcs
                            </td>
                            <td style={{ padding: '8px 10px', fontSize: '12px', fontWeight: '600', color: 'var(--accent-color)' }}>
                              ${Number(ord.total_price || 0).toFixed(2)}
                            </td>
                            <td style={{ padding: '8px 10px', fontSize: '12px' }}>
                              <span
                                style={{
                                  padding: '2px 6px',
                                  borderRadius: '8px',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  background: 'rgba(255, 123, 114, 0.15)',
                                  color: '#ff7b72',
                                  border: '1px solid rgba(255, 123, 114, 0.3)'
                                }}
                              >
                                In Production
                              </span>
                            </td>
                            <td style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute)' }}>
                              {ord.created_at ? new Date(ord.created_at).toLocaleDateString() : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-mute, #8b949e)', fontSize: '12px' }}>
                    No manufacturing orders currently in production.
                  </div>
                )}
              </div>

              {/* ------------------------------------------------------------ */}
              {/* Global Tenant Comparison Grid (when Global Admin)            */}
              {/* ------------------------------------------------------------ */}
              {isGlobal && glMetrics?.tenants_breakdown && (
                <div
                  style={{
                    background: 'var(--card-bg, #161b22)',
                    border: '1px solid var(--border-color, #30363d)',
                    borderRadius: '8px',
                    padding: '14px 16px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: 'var(--accent-color)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                          <line x1="8" y1="21" x2="16" y2="21" />
                          <line x1="12" y1="17" x2="12" y2="21" />
                        </svg>
                        <span>Tenant Performance Breakdown</span>
                      </h4>
                      <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-mute, #8b949e)' }}>
                        Comparative cross-tenant analysis of operators, coins, engagement, and production orders.
                      </p>
                    </div>

                    {selectedTenantId && (
                      <button
                        onClick={() => setSelectedTenantId('')}
                        style={{
                          padding: '4px 10px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid var(--border-color, #30363d)',
                          borderRadius: '6px',
                          color: 'var(--text-main)',
                          fontSize: '11px',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                      >
                        Reset to All Tenants
                      </button>
                    )}
                  </div>

                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '800px' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border-color, #30363d)' }}>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Tenant</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Operators</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Active Coins</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Shares</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Card Clicks</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Booking Clicks</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Prod Orders</th>
                          <th style={{ padding: '8px 10px', fontSize: '11px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {glMetrics.tenants_breakdown.map((t) => {
                          const isSelected = selectedTenantId === t.tenant_id.toString();
                          return (
                            <tr
                              key={t.tenant_id}
                              style={{
                                borderBottom: '1px solid var(--border-color, #30363d)',
                                backgroundColor: isSelected ? 'rgba(160, 196, 255, 0.08)' : 'transparent'
                              }}
                            >
                              <td style={{ padding: '8px 10px', fontSize: '12px' }}>
                                <div style={{ fontWeight: '600', color: isSelected ? 'var(--accent-color)' : 'var(--text-main)' }}>
                                  {t.brand_name}
                                </div>
                                <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                                  {t.domain_name}
                                </div>
                              </td>
                              <td style={{ padding: '8px 10px', fontSize: '12px', color: 'var(--text-main)' }}>
                                {t.operators_count}
                              </td>
                              <td style={{ padding: '8px 10px', fontSize: '12px', color: 'var(--text-main)' }}>
                                <div>{t.active_coins_total}</div>
                                <div style={{ fontSize: '10px', color: '#3fb950' }}>
                                  +{t.active_coins_current_month} {currentMonthLabel.split(' ')[0]}
                                </div>
                              </td>
                              <td style={{ padding: '8px 10px', fontSize: '12px', color: '#e3b341' }}>
                                {t.shares_total}
                              </td>
                              <td style={{ padding: '8px 10px', fontSize: '12px', color: '#58a6ff' }}>
                                {t.card_clicks_total}
                              </td>
                              <td style={{ padding: '8px 10px', fontSize: '12px', color: '#3fb950' }}>
                                {t.booking_clicks_total}
                              </td>
                              <td style={{ padding: '8px 10px', fontSize: '12px', color: '#ff7b72' }}>
                                {t.active_orders_in_production}
                              </td>
                              <td style={{ padding: '8px 10px', fontSize: '12px', textAlign: 'right' }}>
                                <button
                                  onClick={() => handleSelectTenantFromTable(t.tenant_id)}
                                  style={{
                                    padding: '3px 8px',
                                    borderRadius: '4px',
                                    background: isSelected ? 'var(--accent-color)' : 'rgba(255, 255, 255, 0.05)',
                                    color: isSelected ? '#000' : 'var(--text-main)',
                                    border: '1px solid var(--border-color, #30363d)',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                  }}
                                >
                                  {isSelected ? 'Selected' : 'Filter Scope'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            </>
          )}
        </>
      )}
    </div>
  );
};

export default OverviewTab;
